import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, AlertCircle, Loader2, ChevronDown, Mail, Phone, Building2 } from 'lucide-react';
import apiClient from '../../lib/apiClient';
import './Contacts.css';

/*
 * ContactsListTab — all-accounts sortable CRM contact table, mirroring the
 * GAS ContactsListPanel (App.jsx ~5039) with the columns the TekStak data
 * actually has.
 *
 * Endpoint reality check: GAS's list reads GET /api/v1/contacts?all=true,
 * but routes/v1.js requireScope is API-key ONLY (no Supabase-JWT fallback),
 * so that endpoint 401s from this app. The JWT-reachable sources are:
 *   - GET /api/v1/tasks/people ({ people }) — every CRM contact (kind
 *     'contact'): name, email, phone, account acronym. Dual-path auth
 *     (middleware/keyScope.js, optionalKey), already used by TasksPage.
 *   - GET /api/workspace/contact-candidates?status=approved — the harvest
 *     rows that were promoted to contacts: title, sent/received counts,
 *     last_seen_at, matched account. Joined client-side by email to enrich
 *     the people rows (hand-created contacts simply have no harvest data).
 * Neither exposes a usable contacts.id on every row, so rows expand inline
 * with the fields available rather than opening ContactDetailModal (which
 * needs a contact id for its /api/workspace/contacts/:id reads/writes).
 */

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric',
  });
}

export default function ContactsListTab({ departments, onDepartmentSelect, onGoReview }) {
  const [q, setQ] = useState('');
  const [sort, setSort] = useState({ key: 'name', dir: 'asc' });
  const [expandedKey, setExpandedKey] = useState(null);

  // Primary: every CRM contact (limit 300 server-side).
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['crm-people'],
    queryFn: () => apiClient.get('/api/v1/tasks/people'),
    staleTime: 60_000,
  });

  // Enrichment: harvest metadata for promoted candidates, joined by email.
  // Failing here degrades gracefully — the basic columns still render.
  const { data: approvedData, error: approvedError } = useQuery({
    queryKey: ['contact-candidates', 'approved'],
    queryFn: () => apiClient.get('/api/workspace/contact-candidates?status=approved&limit=500'),
    staleTime: 60_000,
  });

  const all = useMemo(() => {
    const people = (data?.people || []).filter((p) => p.kind === 'contact');
    const byEmail = new Map();
    (approvedData?.candidates || []).forEach((c) => {
      if (c.email) byEmail.set(String(c.email).toLowerCase(), c);
    });
    return people.map((p, i) => {
      const cand = p.email ? byEmail.get(String(p.email).toLowerCase()) : null;
      return {
        key: p.email || `${p.name}-${i}`,
        name: p.name,
        email: p.email || '',
        phone: p.phone || cand?.phone || '',
        acronym: p.hint || '',
        title: cand?.title || '',
        accountName: cand?.matched_account_name || '',
        msgs: cand ? (Number(cand.sent_count) || 0) + (Number(cand.received_count) || 0) : 0,
        lastSeenAt: cand?.last_seen_at || null,
        firstSeenAt: cand?.first_seen_at || null,
      };
    });
  }, [data, approvedData]);

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const searched = needle
      ? all.filter((c) =>
          (c.name || '').toLowerCase().includes(needle) ||
          (c.email || '').toLowerCase().includes(needle) ||
          (c.acronym || '').toLowerCase().includes(needle) ||
          (c.accountName || '').toLowerCase().includes(needle) ||
          (c.title || '').toLowerCase().includes(needle))
      : all;
    const sortVal = (c) => {
      switch (sort.key) {
        case 'dept': return (c.acronym || c.accountName || '').toLowerCase();
        case 'title': return (c.title || '').toLowerCase();
        case 'messages': return c.msgs;
        case 'activity': return c.lastSeenAt ? new Date(c.lastSeenAt).getTime() : 0;
        default: return (c.name || '').toLowerCase();
      }
    };
    return [...searched].sort((a, b) => {
      const va = sortVal(a);
      const vb = sortVal(b);
      const cmp = typeof va === 'number' && typeof vb === 'number'
        ? va - vb
        : String(va).localeCompare(String(vb));
      return sort.dir === 'asc' ? cmp : -cmp;
    });
  }, [all, q, sort]);

  const toggleSort = (key) => setSort((s) =>
    s.key === key
      ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' }
      : { key, dir: (key === 'messages' || key === 'activity') ? 'desc' : 'asc' });

  const th = (key, label, extraClass = '') => (
    <th
      className={`contacts-th-sort ${extraClass}`}
      onClick={() => toggleSort(key)}
      aria-sort={sort.key === key ? (sort.dir === 'asc' ? 'ascending' : 'descending') : undefined}
      title="Sort"
    >
      {label}
      {sort.key === key && (
        <span className="contacts-sort-arrow" aria-hidden>{sort.dir === 'asc' ? '↑' : '↓'}</span>
      )}
    </th>
  );

  // Optional MainApp wiring: with departments + onDepartmentSelect present,
  // the acronym badge navigates to that agency's dashboard.
  const deptIdFor = (acronym) => {
    if (!onDepartmentSelect || !acronym || !Array.isArray(departments)) return null;
    const d = departments.find((x) => (x.acronym || '').toLowerCase() === acronym.toLowerCase());
    return d?.department_id ?? null;
  };

  const deptCell = (c) => {
    if (!c.acronym && !c.accountName) return <span className="contacts-muted">—</span>;
    const label = c.acronym
      ? <span className="contacts-badge">{c.acronym}</span>
      : <span className="contacts-muted">{c.accountName}</span>;
    const deptId = deptIdFor(c.acronym);
    if (deptId == null) return label;
    return (
      <button
        type="button"
        className="contacts-dept-link"
        onClick={(e) => { e.stopPropagation(); onDepartmentSelect(deptId); }}
        title="Open this agency"
      >
        {label}
      </button>
    );
  };

  if (isLoading) {
    return (
      <div className="contacts-state" role="status">
        <Loader2 size={18} className="contacts-spin" /> Loading contacts…
      </div>
    );
  }

  if (error) {
    return (
      <div className="contacts-state contacts-state-error" role="alert">
        <AlertCircle size={18} />
        <span>Contacts unavailable: {error.message}</span>
        <button type="button" className="contacts-retry" onClick={() => refetch()}>Retry</button>
      </div>
    );
  }

  return (
    <div className="ts-card contacts-list-card">
      <div className="contacts-list-head">
        <h3 className="ts-card-title">All contacts</h3>
        <span className="contacts-count">{rows.length}</span>
      </div>

      <div className="contacts-search">
        <Search size={15} className="contacts-search-icon" />
        <input
          type="search"
          className="contacts-search-input"
          placeholder="Search name, email, org, title…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          aria-label="Search contacts"
        />
      </div>

      {approvedError && (
        <p className="contacts-degrade-note">
          Harvest metadata unavailable ({approvedError.message}) — showing basic contact fields.
        </p>
      )}

      {rows.length === 0 && (
        <p className="contacts-empty">
          {all.length === 0 ? (
            <>
              No contacts yet — approve harvested candidates in{' '}
              <button type="button" className="contacts-linklike" onClick={onGoReview}>
                Sync &amp; review
              </button>
              , or add people from an account workspace.
            </>
          ) : (
            `No contacts match "${q}".`
          )}
        </p>
      )}

      {rows.length > 0 && (
        <div className="contacts-table-wrap">
          <table className="contacts-table">
            <thead>
              <tr>
                {th('name', 'Name')}
                <th>Email</th>
                {th('dept', 'Department')}
                {th('title', 'Title')}
                {th('messages', 'Msgs', 'contacts-th-num')}
                {th('activity', 'Last activity', 'contacts-th-num')}
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => {
                const expanded = expandedKey === c.key;
                return (
                  <React.Fragment key={c.key}>
                    <tr
                      className={`contacts-row ${expanded ? 'is-expanded' : ''}`}
                      onClick={() => setExpandedKey(expanded ? null : c.key)}
                    >
                      <td data-label="Name">
                        <span className="contacts-name">
                          {c.name}
                          <ChevronDown size={12} className={`contacts-chevron ${expanded ? 'is-open' : ''}`} aria-hidden />
                        </span>
                      </td>
                      <td data-label="Email" className="contacts-muted">{c.email || '—'}</td>
                      <td data-label="Department">{deptCell(c)}</td>
                      <td data-label="Title" className="contacts-muted">{c.title || '—'}</td>
                      <td data-label="Msgs" className="contacts-num">{c.msgs || '—'}</td>
                      <td data-label="Last activity" className="contacts-num contacts-muted">
                        {fmtDate(c.lastSeenAt)}
                      </td>
                    </tr>
                    {expanded && (
                      <tr className="contacts-detail-row">
                        <td colSpan={6}>
                          <div className="contacts-detail">
                            {c.email && (
                              <span className="contacts-detail-item">
                                <Mail size={13} /> <a href={`mailto:${c.email}`} onClick={(e) => e.stopPropagation()}>{c.email}</a>
                              </span>
                            )}
                            {c.phone && (
                              <span className="contacts-detail-item">
                                <Phone size={13} /> <a href={`tel:${c.phone}`} onClick={(e) => e.stopPropagation()}>{c.phone}</a>
                              </span>
                            )}
                            {(c.accountName || c.acronym) && (
                              <span className="contacts-detail-item">
                                <Building2 size={13} /> {c.accountName || c.acronym}
                              </span>
                            )}
                            {c.firstSeenAt && (
                              <span className="contacts-detail-item contacts-muted">
                                First seen {fmtDate(c.firstSeenAt)}
                              </span>
                            )}
                            {c.msgs > 0 && (
                              <span className="contacts-detail-item contacts-muted">
                                {c.msgs} messages harvested
                              </span>
                            )}
                            <span className="contacts-detail-item contacts-muted">
                              Full profile editing lives in the account workspace org chart.
                            </span>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
